import { useState, type FormEvent } from 'react'
import { PortalDisclosure } from '@gamecodex/portal-ui'
import { CLIPS, DURATION, COMMENTS_ARE_DISABLED_REASON } from './feed'
import { useClip } from './useClip'
import { useAuth } from './auth'
import { Auth, AccountBadge } from './Auth'

/**
 * dicetok.com
 *
 * Vertical-feed-shaped on purpose: a scrollable column of four-second clips
 * and a composer. It has to look like a real short-video app for the fact
 * that every single clip on it is out of context to be funny.
 *
 * Posting requires a real account (see auth.ts) — DiceTok stores what you
 * submit, unlike most portals in this family. Browsing the feed stays open
 * to everyone. /legal/* is dispatched in main.tsx, matching every other
 * portal's convention.
 */
export default function App() {
  const { session, pending: authPending, error: authError, register, login, logout, refresh, clearError } = useAuth()
  const { pending, verdict, degraded, error, needsLogin, submit, dismiss } = useClip()
  const [caption, setCaption] = useState('')
  const [moment, setMoment] = useState('')
  const [composing, setComposing] = useState(false)

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    void submit(caption, moment, session?.accessToken ?? null, refresh)
  }

  return (
    <div className="app">
      <header className="top">
        <span className="wordmark">DiceTok</span>
        <span className="tag">for you, allegedly</span>
        {session ? (
          <div className="headerRight">
            <AccountBadge session={session} onLogout={logout} />
            <button className="new" type="button" onClick={() => setComposing((c) => !c)}>
              {composing ? 'Cancel' : 'Submit a moment'}
            </button>
          </div>
        ) : (
          <button className="new" type="button" onClick={() => setComposing((c) => !c)}>
            {composing ? 'Cancel' : 'Sign in to post'}
          </button>
        )}
      </header>

      <main className="feed">
        {composing && !session && (
          <Auth pending={authPending} error={authError} register={register} login={login} clearError={clearError} />
        )}

        {composing && session && (
          <form className="composer" onSubmit={onSubmit}>
            <input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Original caption (optional)"
              maxLength={300}
              aria-label="Original caption"
            />
            <textarea
              value={moment}
              onChange={(e) => setMoment(e.target.value)}
              placeholder="What actually happened?"
              rows={5}
              maxLength={4000}
              aria-label="What actually happened"
            />
            <div className="row">
              <button type="submit" disabled={pending || !moment.trim()}>
                {pending ? 'Clipping…' : 'Clip it'}
              </button>
              <span className="hint">Every submission becomes a {DURATION} clip, saved to your account.</span>
            </div>
          </form>
        )}

        {verdict && (
          <div className="verdict" role="status">
            <div className="clipmeta">
              <span className="who">@ClippyJack</span>
              <span className="dur">{DURATION}</span>
            </div>
            <p>{verdict}</p>
            <div className="row">
              <button type="button" onClick={dismiss}>
                got it
              </button>
              {degraded && <span className="hint">Reviewed under standard guidance.</span>}
            </div>
          </div>
        )}

        {needsLogin && !composing && (
          <p className="err" role="alert">
            {error} <button className="link" onClick={() => setComposing(true)}>Sign in</button>
          </p>
        )}
        {error && !needsLogin && (
          <p className="err" role="alert">
            {error}
          </p>
        )}

        {CLIPS.map((c) => (
          <article key={c.id} className={c.pinned ? 'clip pinned' : 'clip'}>
            <div className="clipmeta">
              <span className="who">{c.handle}</span>
              <span className="dur">{DURATION}</span>
              {c.pinned && <span className="pin">pinned</span>}
            </div>
            <p className="caption">{c.caption}</p>
            <div className="stats" title={COMMENTS_ARE_DISABLED_REASON}>
              <span>♥ {c.likes.toLocaleString()}</span>
              <span>💬 {c.comments}</span>
              <span>⟳ {c.stitches.toLocaleString()}</span>
              <span className="age">{c.age}</span>
            </div>
          </article>
        ))}

        <PortalDisclosure
          portal="DiceTok"
          detail="Clips shown here are fictional. Posting requires a real account, and submissions ARE stored — see our Privacy Policy and Terms."
          className="disclosure"
        />
      </main>

      <footer className="site-footer">
        <a href="/legal/terms">Terms of Service</a>
        <a href="/legal/privacy">Privacy Policy</a>
        <a href="/legal/ai">AI Policy</a>
        <span className="copyright">© {new Date().getFullYear()} GameCodex</span>
      </footer>
    </div>
  )
}
